/* eslint-disable no-use-before-define */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Collapse } from 'react-bootstrap';
import classnames from 'classnames';

import BomModal from 'shared/product_tabs/tab_parts/components/BomModal';
import BomReview from 'shared/product_tabs/tab_parts/components/review/BomReview';
import BomSample from 'shared/product_tabs/tab_parts/components/default/BomSample';
import ExtractZipFileTool from 'shared/product_tabs/tab_parts/components/upload/ExtractZipFileTool';
import PartsSourcing from 'shared/product_tabs/tab_parts/components/sourcing/PartsSourcing';
import SearchModal from 'shared/product_tabs/tab_parts/components/search/SearchModal';

import {
  QUOTED,
  QUOTING_BOM_PARTS,
} from 'shared/product_tabs/tab_parts/data/BomStatus';

import {
  openModal,
  closeModal,
  clearMessages,
  cleanUpBomData,
  replacePart,
  replacePartSupplier,
  replaceBomData,
  saveBomData,
  updateShowBomSelection,
} from 'shared/product_tabs/tab_parts/bomSlice';

import { selectDataForTabView } from 'shared/product_tabs/tab_parts/slice/selectors';
import ShowMoreOptions from 'shared/product_tabs/tab_parts/components/admin/more_options/ShowMoreOptions';

const Parts = () => {
  const dispatch = useDispatch();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showMoreOptionsModal, setShowMoreOptionsModal] = useState(false);
  const [errorMessage] = useState(null);
  const [showError] = useState(false);
  const [currentBomLineItem, setCurrentBomLineItem] = useState(null);
  const [oldBomData, setOldBomData] = useState(null);

  // eslint-disable-next-line react-redux/useSelector-prefer-selectors
  const bom = useSelector((state) => state.bom);

  const {
    id,
    bom_line_items,
    bom_parameters,
    bom_status,
    loading,
    markups,
    messages,
    saved,
    selected_board_qty,
    substitutes,
    show_modal,
    show_bom_selection,
    uploaded_zip_file,
    bom_selected,
    available_bom_in_zip,
    step,
    total_steps,
  } = useSelector(selectDataForTabView);

  const onCloseZipFileTool = () => {
    setOldBomData(null);
  };

  const onShow = () => {
    if (!oldBomData && saved && bom_status !== QUOTING_BOM_PARTS) {
      setOldBomData(bom);
    }
  };

  const onHide = () => {
    dispatch(cleanUpBomData(false));
    dispatch(clearMessages());
    onCloseBomModal();
  };

  const onSaveChanges = () => {
    dispatch(saveBomData(id));
  };

  const onCancelChanges = () => {
    if (oldBomData) {
      dispatch(replaceBomData(oldBomData));
      setOldBomData(null);
    } else {
      dispatch(cleanUpBomData(true));
    }
    dispatch(clearMessages());
    onCloseBomModal();
  };

  const onShowSearchModal = (bom_line_item) => {
    setCurrentBomLineItem(bom_line_item);
    setShowSearchModal(true);
    onCloseBomModal();
  };

  const onCloseSearchModal = () => {
    dispatch(clearMessages());
    setCurrentBomLineItem(null);
    setShowSearchModal(false);
    onOpenBomModal();
  };

  const onShowMoreOptions = (bom_line_item) => {
    setCurrentBomLineItem(bom_line_item);
    setShowMoreOptionsModal(true);
    onCloseBomModal();
  };

  const onCloseShowMoreOptions = () => {
    dispatch(clearMessages());
    setCurrentBomLineItem(null);
    setShowMoreOptionsModal(false);
    onOpenBomModal();
  };

  const onReplacePart = (part) => {
    if (!currentBomLineItem) return;
    dispatch(replacePart({ bom_line_item_id: currentBomLineItem.id, part }));
    setCurrentBomLineItem(null);
    setShowSearchModal(false);
    onOpenBomModal();
  };

  const onReplacePartSupplier = (part, part_supplier) => {
    if (!currentBomLineItem) return;
    dispatch(
      replacePartSupplier({
        bom_line_item_id: currentBomLineItem.id,
        part,
        part_supplier,
      }),
    );
    setCurrentBomLineItem(null);
    setShowMoreOptionsModal(false);
    onOpenBomModal();
  };

  const onOpenBomModal = () => {
    dispatch(openModal());
  };

  const onCloseBomModal = () => {
    dispatch(closeModal());
  };

  const onShowBomSelectionModal = () => {
    dispatch(updateShowBomSelection(true));
  };

  return (
    <>
      {!show_modal && errorMessage && (
        <Collapse in={showError}>
          <div>
            <Alert
              variant='danger'
              className='fade show'
              style={{ transition: 'opacity 0.5s ease-in-out' }}>
              {errorMessage}
            </Alert>
          </div>
        </Collapse>
      )}
      <div className='row'>
        <div
          className={classnames('col-12 parts-title-block mb-4', {
            'd-none': uploaded_zip_file && bom_selected,
          })}>
          {!uploaded_zip_file && (
            <span>
              To quote your electronic components online, please upload a Zip
              File with your Gerbers and BOM in Excel format.
            </span>
          )}
          {uploaded_zip_file && !available_bom_in_zip && (
            <span>
              The Zip File that you uploaded does not contain an Excel file.
              <br />
              Please upload a new Zip File with your Gerbers and BOM in excel
              format.
            </span>
          )}
          {uploaded_zip_file &&
            available_bom_in_zip &&
            !bom_selected &&
            !show_bom_selection &&
            (<>
              <div className='mb-1'>
                <span>
                  Please select your BOM from your Zip File to quote your
                  electronic components.
                </span>
              </div>
              <button
                type='button'
                className='btn btn-outline-primary bom-file-button'
                onClick={onShowBomSelectionModal}>
                Select BOM
              </button>
            </>)}
        </div>
        <div
          className={classnames('col-12', {
            'mt-3':
              !uploaded_zip_file ||
              (uploaded_zip_file && !available_bom_in_zip),
          })}>
          {!bom_selected && <BomSample />}
          {bom_selected &&
            (bom_status === QUOTING_BOM_PARTS ||
              (bom_status === QUOTED && !saved)) &&
            !show_modal &&
            (<>
              <div className='mb-1'>
                <span className='fw-bold'>
                  {bom_status === QUOTING_BOM_PARTS
                    ? `Quote Progress...${Math.ceil((step / total_steps) * 100)}%`
                    : 'BOM Quoted'}
                </span>
              </div>
              <button
                type='button'
                className='btn btn-outline-primary bom-file-button'
                onClick={onOpenBomModal}>
                View {bom_status === QUOTING_BOM_PARTS ? 'Progress' : 'Quote'}
              </button>
            </>)
          }
          <BomReview onOpenBomModal={onOpenBomModal} />
          <PartsSourcing />
        </div>
      </div>
      <ExtractZipFileTool
        onShow={onShow}
        onCloseZipFileTool={onCloseZipFileTool} />
      <BomModal
        bom_line_items={bom_line_items}
        bom_parameters={bom_parameters}
        bom_status={bom_status}
        loading={loading}
        messages={messages}
        saved={saved}
        selected_board_qty={selected_board_qty}
        show={show_modal}
        onHide={onHide}
        onShow={onShow}
        onShowSearchModal={onShowSearchModal}
        onShowMoreOptions={onShowMoreOptions}
        onSaveChanges={onSaveChanges}
        onCancelChanges={onCancelChanges} />
      <SearchModal
        bom_line_item={currentBomLineItem}
        loading={loading}
        markups={markups}
        messages={messages}
        substitutes={substitutes}
        selected_board_qty={selected_board_qty}
        show={showSearchModal}
        onHide={onCloseSearchModal}
        onReplacePart={onReplacePart} />
      {isManager && (
        <ShowMoreOptions
          bom_line_item={currentBomLineItem}
          loading={loading}
          markups={markups}
          messages={messages}
          substitutes={substitutes}
          selected_board_qty={selected_board_qty}
          show={showMoreOptionsModal}
          onHide={onCloseShowMoreOptions}
          onReplacePartSupplier={onReplacePartSupplier} />
      )}
    </>
  );
};

export default Parts;
